import { useEffect, useMemo, useState } from 'react';
import { DIRECTIONS, coordKey, hexRegion, loadLevel, applySlide, legalDirections, checkWin, type AxialCoord, type Board as BoardT, type HexDirection, type LevelDef, type TileColor } from '@tessera/engine';
import { solve } from '@tessera/solver';
import { axialToPixel, hexCorners, hexPath, tileFill, TILE_SIZE } from '../hex-layout';
import { sfx } from '../audio';
import { haptics } from '../haptics';
import { CloseIcon, HintIcon, UndoIcon } from './Icons';
import { HexCritter } from './HexCritter';

// W14 Ballast: rotation angle (degrees) for the gravity arrow per direction.
// Derived from axialToPixel — for each direction, angle = atan2(Δy, Δx) of
// the pixel offset from a cell to its neighbor in that direction. Pre-
// computed because axialToPixel uses sqrt(3) and the angles are static.
const GRAVITY_ROT_DEG: Record<HexDirection, number> = {
  E: 0,
  NE: -60,
  NW: -120,
  W: 180,
  SW: 120,
  SE: 60,
};

interface Props {
  level: LevelDef;
  // `hintUsed` flags whether the player consumed a hint on this attempt. The
  // app-level code uses it to skip best-moves tracking for hinted clears —
  // hints should feel generous, but they can't count for your personal par.
  onSolved: (movesUsed: number, hintUsed: boolean) => void;
  onLost: (movesUsed: number) => void;
  onMoves: (n: number) => void;
  // W15 Cairn: how many target cells are currently satisfied (have a tile
  // of the matching color). Emitted on mount and after every slide for
  // target-objective levels; never called for 'clear' levels. Parent can
  // ignore this for non-target worlds (it stays at its initial value).
  onTargetsChanged?: (satisfied: number) => void;
}

const DIR_LABEL: Record<HexDirection, string> = {
  E: '→', W: '←',
  NE: '↗', NW: '↖',
  SE: '↘', SW: '↙',
};

interface MergeFx {
  key: number;
  events: { cells: readonly AxialCoord[]; color: TileColor; resultCoord: AxialCoord; chainDepth: number }[];
}

export function Board({ level, onSolved, onLost, onMoves, onTargetsChanged }: Props) {
  const [board, setBoard] = useState(() => loadLevel(level));
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [moves, setMoves] = useState(0);
  const [mergeFx, setMergeFx] = useState<MergeFx>({ key: 0, events: [] });
  const [history, setHistory] = useState<BoardT[]>([]);
  const [hintTileId, setHintTileId] = useState<number | null>(null);
  const [hintUsed, setHintUsed] = useState(false);
  const [hintThinking, setHintThinking] = useState(false);
  // Multi-merge announcer — a brief toast that fires when a single slide
  // triggers more than one merge event (either a chain-annihilation or
  // multi-color cascade from shifted tiles forming groups elsewhere on
  // the board). Surfaces the "wait, something else just cleared" moment
  // that would otherwise be invisible cause-and-effect.
  const [cascadeToast, setCascadeToast] = useState<string | null>(null);
  // Tile IDs that start the level already adjacent to 2+ same-color mates.
  // Any first slide, regardless of which tile moves, will trigger their
  // merge (engine runs resolveMerges after every slide). We pulse a ring
  // around these tiles for ~2s at level load so the player sees them
  // coming — before they move and wonder what just happened.
  const [preExistingMergeIds, setPreExistingMergeIds] = useState<ReadonlySet<number>>(new Set());
  // Loss-diagnostic overlay. Non-null during the ~1s explanation animation
  // that plays right before the "out of moves" dialog. See handleSlide's
  // lost branch for how this is set and the render block near the bottom
  // for what it draws (per-color count badges + pulse ring on each tile).
  const [lossIntroKey, setLossIntroKey] = useState<number | null>(null);

  // W15 Cairn: track satisfied targets and notify parent on every board
  // change. Cheap O(targets × 1) lookup against board.tileAt. Fires on
  // mount and every slide thanks to the [board, level] dependency. For
  // non-target objectives this effect short-circuits without notification
  // (parent is responsible for ignoring stale satisfied counts when the
  // level is 'clear', which is trivially the case since they never wire
  // the prop for 'clear' worlds in the first place).
  useEffect(() => {
    if (level.objective.kind !== 'target' || !onTargetsChanged) return;
    const targets = level.objective.targets;
    let satisfied = 0;
    for (const t of targets) {
      const tid = board.tileAt.get(coordKey(t.coord));
      if (tid !== undefined) {
        const tile = board.tiles.get(tid);
        if (tile && tile.color === t.color) satisfied++;
      }
    }
    onTargetsChanged(satisfied);
  }, [board, level, onTargetsChanged]);

  // Reset internal state whenever the level prop changes (e.g. Next Level tap).
  useEffect(() => {
    setBoard(loadLevel(level));
    setSelectedId(null);
    setMoves(0);
    setMergeFx({ key: 0, events: [] });
    setHistory([]);
    setHintTileId(null);
    setHintUsed(false);
    setHintThinking(false);
    setCascadeToast(null);
    setLossIntroKey(null);
    // Scan the fresh board for pre-existing mergeable groups. If we find
    // any, mark their tile IDs so the render adds a pulsing ring for the
    // first ~2s of the level — a "heads up, these will merge on your
    // first move, even if you touch something else" tell.
    const freshBoard = loadLevel(level);
    const groups = findMergeableGroupsOnBoard(freshBoard);
    if (groups.length > 0) {
      const ids = new Set<number>();
      for (const g of groups) for (const id of g) ids.add(id);
      setPreExistingMergeIds(ids);
      const clearId = window.setTimeout(() => setPreExistingMergeIds(new Set()), 2400);
      return () => window.clearTimeout(clearId);
    }
    setPreExistingMergeIds(new Set());
  }, [level]);

  // Keyboard shortcuts for desktop / keyboard players. We keep the set small
  // and consistent with common puzzle-game conventions:
  //   Z, Ctrl+Z, U, Backspace → undo one move
  //   H                       → ask for a hint
  //   Escape                  → deselect
  // Ignored when the player is typing in an input/textarea (belt-and-suspenders;
  // we don't actually render any today, but this keeps things safe if we do).
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const target = e.target as HTMLElement | null;
      const tag = target?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || target?.isContentEditable) return;
      // Ignore modifier combos other than the documented Ctrl+Z.
      const isCtrlZ = (e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'z';
      if (!isCtrlZ && (e.ctrlKey || e.metaKey || e.altKey)) return;
      const key = e.key.toLowerCase();
      if (key === 'escape') {
        setSelectedId(null);
        setHintTileId(null);
      } else if (isCtrlZ || key === 'z' || key === 'u' || key === 'backspace') {
        e.preventDefault();
        handleUndo();
      } else if (key === 'h' || key === '?') {
        e.preventDefault();
        handleHint();
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // Intentionally re-register each render so the handlers close over fresh
    // state. Cheap — keydown listeners are not hot-path.
  });

  const size = TILE_SIZE;
  // cellPositions = full hex region. cellPositionsInBoard = filtered to
  // exclude holes. We use the FULL set for bounds (so the viewport stays
  // centered on the hex region's geometric center even when holes are
  // asymmetric — Crescent, Island patterns) but the FILTERED set for
  // rendering empty cells (so holes don't visually appear as playable cells).
  // W16 Riven introduces non-trivial hole patterns; W1-W15 levels have no
  // holes, so this filter is a no-op for them.
  const cellPositions = useMemo(() => hexRegion(level.radius).map((c) => ({ c, p: axialToPixel(c) })), [level.radius]);
  const cellPositionsInBoard = useMemo(() => {
    const holes = new Set(level.holes.map(coordKey));
    return cellPositions.filter(({ c }) => !holes.has(coordKey(c)));
  }, [cellPositions, level.holes]);
  const bounds = useMemo(() => {
    const pad = size * 1.4;
    const xs = cellPositions.map(({ p }) => p.x);
    const ys = cellPositions.map(({ p }) => p.y);
    const minX = Math.min(...xs) - pad, maxX = Math.max(...xs) + pad;
    const minY = Math.min(...ys) - pad, maxY = Math.max(...ys) + pad;
    return { minX, minY, w: maxX - minX, h: maxY - minY };
  }, [cellPositions, size]);

  const cornerPath = useMemo(() => hexPath(size - 2), [size]);
  const selectedTile = selectedId !== null ? board.tiles.get(selectedId) : null;
  const selectedDirs: HexDirection[] = selectedTile ? legalDirections(board, selectedTile.id) : [];

  function handleTileTap(id: number) {
    // Locked + mixer tiles are non-interactive — neither can be slid.
    // Selecting one would surface no legal directions and dead-end the
    // selection ring. Reject at the tap so the interaction reads as
    // "you can't move this," consistent with the visual treatment.
    // (Mixers are claimed when a colored tile slides INTO them; players
    // never select a mixer themselves.)
    const tile = board.tiles.get(id);
    if (tile?.state.kind === 'locked' || tile?.state.kind === 'mixer') return;
    setSelectedId((cur) => {
      const nextSel = cur === id ? null : id;
      if (nextSel !== null) { sfx.select(); haptics.tap(); }
      return nextSel;
    });
  }

  function handleSlide(dir: HexDirection) {
    if (selectedId === null) return;
    const res = applySlide(board, selectedId, dir);
    if (res.movesUsed === 0) return;
    const next = res.board;
    const nextMoves = moves + 1;
    setHistory((h) => [...h, board]);
    setBoard(next);
    setMoves(nextMoves);
    setSelectedId(null);
    // Hint was about the pre-slide board; once the player acts, the highlight
    // is stale. Clear it so the ring doesn't linger on an unrelated tile.
    setHintTileId(null);
    onMoves(nextMoves);
    sfx.slide();
    haptics.slide();
    if (res.merges.length > 0) {
      const maxChain = res.merges.reduce((m, e) => Math.max(m, e.chainDepth), 0);
      sfx.merge(maxChain);
      haptics.merge(maxChain);
      const fx: MergeFx = { key: Date.now(), events: res.merges.map((e) => ({ ...e })) };
      setMergeFx(fx);
      // FX is 600ms hold + 320ms collapse + overlapping accent ring, total
      // ~960ms for chain annihilation. 1050ms leaves a small buffer before
      // the node unmounts.
      window.setTimeout(() => {
        setMergeFx((cur) => (cur.key === fx.key ? { key: fx.key + 1, events: [] } : cur));
      }, 1050);
      // Cascade announcer: show a toast when merges happen that aren't
      // obviously caused by the tile the player just moved.
      //   - merges.length > 1: cascade (multiple groups resolved together)
      //   - chainDepth >= 2: annihilation chain (charged tile absorbed into
      //     another merge)
      //   - single merge in a DIFFERENT COLOR than the slid tile: "auto-
      //     merge" — the player moved tile X and a group of Y cleared,
      //     which reads as mysterious without surfacing it. Common on
      //     first moves of levels that have pre-existing adjacent groups.
      const totalCleared = res.merges.reduce((n, e) => n + e.cells.length, 0);
      const slidColor = board.tiles.get(selectedId)?.color;
      let toastMsg: string | null = null;
      if (res.merges.length > 1) {
        toastMsg = `Cascade · ${totalCleared} cleared`;
      } else if (maxChain >= 2) {
        toastMsg = `Chain · ${totalCleared} annihilated`;
      } else if (
        res.merges.length === 1 &&
        slidColor !== undefined &&
        res.merges[0]!.color !== slidColor
      ) {
        toastMsg = `Auto-merge · ${totalCleared} cleared`;
      }
      if (toastMsg) {
        const tKey = Date.now();
        setCascadeToast(toastMsg);
        window.setTimeout(() => {
          setCascadeToast((cur) => (cur === toastMsg ? null : cur));
        }, 1500);
        // Silence a TS "unused" in case future edits drop tKey.
        void tKey;
      }
    }
    // Compute the game state. We promote three extra conditions to 'lost'
    // so the player isn't stuck wandering a doomed board:
    //
    //   (a) checkWin's own 'lost' — move budget exhausted.
    //   (b) "no legal slide anywhere" deadlock — rare; happens on
    //       densely packed fragments.
    //   (c) "no merge will ever be possible" for the 'clear' objective —
    //       if no color has at least 3 mergeable tiles left on the board,
    //       the player can't form a group of 3+ same-color connected
    //       (the minimum merge group), so 'clear' is mathematically
    //       unreachable. Common triggers: a lone tile, two stranded
    //       charged tiles of different colors, five tiles split five
    //       ways. The player can still slide, but every slide is wasted;
    //       better to snap them to the restart menu immediately.
    //
    // We deliberately don't try to detect connectivity — "I have 5 red
    // but they can never reach each other" requires solver-level search
    // and isn't worth the cost for a rare case. The move-budget guard
    // catches those eventually.
    let state = checkWin(next, level.objective, nextMoves);
    if (state === 'ongoing' && next.tiles.size > 0) {
      let anyLegal = false;
      for (const tile of next.tiles.values()) {
        if (legalDirections(next, tile.id).length > 0) { anyLegal = true; break; }
      }
      if (!anyLegal) state = 'lost';
    }
    if (
      state === 'ongoing' &&
      level.objective.kind === 'clear' &&
      next.tiles.size > 0 &&
      // Skip the no-mergeable-groups heuristic for W11+ entirely.
      // The mechanic-rich expansion worlds (locked tiles, mixers,
      // gravity, future fragile/wind/tether) have too many state-
      // mutation paths for any static color-count heuristic to be
      // reliable. Move-budget exhaustion + no-legal-slide-anywhere are
      // the only safe lose-conditions there. The heuristic remains in
      // effect for W1-W10 (free + first paid tier) where it correctly
      // catches genuine "stranded the last cluster" deadlocks early
      // and saves the player from playing a doomed board.
      level.world <= 10
    ) {
      // Count tiles that COULD eventually contribute to a merge group.
      // Original logic counted only 'normal' + 'charged', which marked
      // W11/W12/W13 levels as instantly lost on the first move because
      // locked + mixer tiles weren't tallied — but those states mutate
      // during play (locked → normal via adjacency unlock; mixer →
      // normal of slid-tile color via bump). The corrected model:
      //
      //   - Locked tile of color C: counts as 1 toward color C, because
      //     it WILL become a 'normal' of that color when an adjacent
      //     same-color charged lands. Pre-counting is safe — even if
      //     the player can't pull off the unlock, they'll lose via
      //     move-budget exhaustion, not this early-deadlock heuristic.
      //   - Mixer tile: colorless until claimed, but any color can
      //     claim it — so a mixer adds 1 to a wildcard pool that can
      //     boost any color's count to reach the 3-tile threshold.
      //   - Fragile tile of color C: counts as 1 toward color C until
      //     it breaks (in W8+ Halcyon when shipped — currently W14 is
      //     gravity which doesn't introduce fragile, so this branch is
      //     defensive for v1.x).
      //   - Normal + charged: count as before.
      //
      // The check stays a useful UX optimization for the simple-tile
      // worlds (W1-W10) — players who genuinely strand themselves
      // get the early-deadlock signal — without false-positiving
      // mechanic-rich worlds.
      const counts = new Map<number, number>();
      let mixerWildcards = 0;
      for (const tile of next.tiles.values()) {
        if (tile.state.kind === 'mixer') {
          mixerWildcards++;
          continue;
        }
        // 'normal', 'charged', 'locked', 'fragile' all have a defined
        // color and either are or eventually-could-be merge-eligible.
        counts.set(tile.color, (counts.get(tile.color) ?? 0) + 1);
      }
      let anyMergeable = false;
      for (const c of counts.values()) {
        if (c + mixerWildcards >= 3) { anyMergeable = true; break; }
      }
      if (!anyMergeable) state = 'lost';
    }
    if (state === 'won') {
      // v0.4.1 retired the bee swarm (mascot rule violation). The win
      // moment is now carried entirely by the merge FX + won-screen
      // typography. Merge FX is 600ms HOLD + 320ms collapse (~960ms max).
      //   - tiles==0 (board cleared): 1150ms — full FX + ~200ms breath
      //   - merges>0 tiles remain: 1000ms — right as the FX settles
      //   - no merges, tiles gone (slide-off clear): 240ms snappy
      const solvedDelay = next.tiles.size === 0
        ? 1150
        : res.merges.length > 0
          ? 1000
          : 240;
      // Capture hintUsed at move-time so the callback sees a stable snapshot
      // even if React schedules a rerender between now and the timeout.
      const wasHinted = hintUsed;
      window.setTimeout(() => { sfx.win(); haptics.win(); onSolved(nextMoves, wasHinted); }, solvedDelay);
    } else if (state === 'lost') {
      // Two-stage lost flow:
      //   1. Clear any in-flight merge fx so the board goes quiet — no
      //      lingering glow from the move that just happened.
      //   2. Kick off the loss-diagnostic overlay (see lossIntroKey): for
      //      ~900ms each tile pulses with a color-matched ring and shows
      //      a count-of-same-color badge. That visually explains *why*
      //      the board is unwinnable — in the 'no mergeable groups' case
      //      every badge is < 3 (amber); for budget-exhaustion the player
      //      sees the final configuration frozen; for no-legal-moves the
      //      badges give something to read instead of an abrupt cut.
      //   3. After 1100ms, fire the lose SFX/haptic and call onLost so
      //      the dialog appears. The explanation is the beat before the
      //      verdict — the old 260ms snap was correct in spirit but too
      //      silent about the reason.
      setMergeFx((cur) => ({ key: cur.key + 1, events: [] }));
      setLossIntroKey(Date.now());
      window.setTimeout(() => { sfx.lose(); haptics.lose(); onLost(nextMoves); }, 1600);
    }
  }

  function handleUndo() {
    if (history.length === 0) return;
    const prev = history[history.length - 1]!;
    setHistory((h) => h.slice(0, -1));
    setBoard(prev);
    setSelectedId(null);
    setMergeFx({ key: Date.now(), events: [] });
    setHintTileId(null);
    const nextMoves = Math.max(0, moves - 1);
    setMoves(nextMoves);
    onMoves(nextMoves);
  }

  // One hint per level. Runs the IDA* solver on the current board at a tight
  // budget, then highlights (and auto-selects) the tile that should move first
  // on the optimal path. The player still picks the direction themselves.
  //
  // Clicking the button while a hint is visible dismisses the highlight — the
  // hint still counts as used, but the pulsing ring goes away so it doesn't
  // nag. Once dismissed, the button disables for the rest of the level.
  function handleHint() {
    if (hintTileId !== null) {
      setHintTileId(null);
      return;
    }
    if (hintUsed || hintThinking) return;
    setHintThinking(true);
    // Kick the solver on next frame so the button visually acknowledges the
    // tap even if the solve is near-instant.
    window.setTimeout(() => {
      try {
        const result = solve(board, level.objective, { maxDepth: 16, maxNodes: 600_000, maxMs: 1_500 });
        if (result.solution && result.solution.length > 0) {
          const step = result.solution[0]!;
          // Find the tile at that coordinate in the current board.
          const tile = [...board.tiles.values()].find((t) => t.coord.q === step.from.q && t.coord.r === step.from.r);
          if (tile) {
            setHintTileId(tile.id);
            setSelectedId(tile.id);
            setHintUsed(true);
            sfx.select();
          }
        }
      } finally {
        setHintThinking(false);
      }
    }, 30);
  }

  const selectedPixel = selectedTile ? axialToPixel(selectedTile.coord) : null;

  const canUndo = history.length > 0;

  return (
    <div className="board-wrap">
      <div className="board-controls">
        <button
          className="icon-btn small-btn"
          onClick={handleUndo}
          disabled={!canUndo}
          aria-label="undo last move"
          title="Undo"
        >
          <UndoIcon />
        </button>
        <button
          className="icon-btn small-btn"
          onClick={handleHint}
          disabled={hintThinking || (hintUsed && hintTileId === null)}
          aria-label={hintTileId !== null ? 'dismiss hint' : 'show a hint'}
          title={
            hintThinking
              ? 'Thinking…'
              : hintTileId !== null
                ? 'Dismiss hint'
                : hintUsed
                  ? 'Hint used'
                  : 'Hint'
          }
        >
          {hintThinking ? '…' : hintTileId !== null ? <CloseIcon /> : <HintIcon />}
        </button>
      </div>
      <svg
        viewBox={`${bounds.minX} ${bounds.minY} ${bounds.w} ${bounds.h}`}
        width="100%"
        style={{ maxHeight: '72vh' }}
        onClick={(e) => { if (e.target === e.currentTarget) setSelectedId(null); }}
      >
        {cellPositionsInBoard.map(({ c, p }) => (
          <g key={coordKey(c)} transform={`translate(${p.x} ${p.y})`}>
            <path d={cornerPath} className="empty-cell" />
          </g>
        ))}
        {/* W14 Ballast: gravity arrows. Rendered between the empty-cell layer
            and the tile layer so a tile sitting on a gravity cell visually
            covers the arrow (the arrow is terrain; the tile is the focus
            object). When the cell is empty, the arrow shows as a muted ink
            chevron pointing in the gravity direction. */}
        {[...board.gravity.entries()].map(([k, dir]) => {
          const [qStr, rStr] = k.split(',');
          const p = axialToPixel({ q: Number(qStr), r: Number(rStr) });
          const angle = GRAVITY_ROT_DEG[dir];
          const L = size * 0.32;  // arrow half-length, leaves cell-edge margin
          const h = size * 0.13;  // chevron arm length
          const arrowD = `M ${-L} 0 L ${L} 0 M ${L - h} ${-h} L ${L} 0 L ${L - h} ${h}`;
          return (
            <g key={`grav-${k}`} transform={`translate(${p.x} ${p.y}) rotate(${angle})`} aria-hidden>
              <path
                d={arrowD}
                fill="none"
                stroke="#3a3a3a"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                opacity="0.55"
              />
            </g>
          );
        })}
        {/* W17 Gale: wind cells. Same {coord, direction} shape as gravity
            but drawn with a curved-chevron glyph so they're visually
            distinct from gravity's straight arrow. Wind fires when a tile
            lands on the cell ADJACENT to a wind source (specifically the
            cell at step(windCell, direction)) — but the player only needs
            to know "wind blows in this direction," same mental model as
            gravity. Rendered between gravity and target layers, BEFORE
            tiles, so a tile sitting on a wind source visually covers it.
            Opacity 0.55 matches gravity for consistency. */}
        {[...board.wind.entries()].map(([k, dir]) => {
          const [qStr, rStr] = k.split(',');
          const p = axialToPixel({ q: Number(qStr), r: Number(rStr) });
          const angle = GRAVITY_ROT_DEG[dir];
          // Wind glyph: curved arc + chevron tip, suggesting "drift" rather
          // than "fall." The arc is a quarter-circle at the back tail so
          // the eye reads it as wind drift; the chevron tip lands at the
          // cell edge to point toward the wind-target.
          const L = size * 0.32;
          const h = size * 0.13;
          // Two stacked curves give the "breeze" feel without being noisy.
          const windD = `M ${-L} ${-h * 0.6} Q ${-L * 0.3} ${-h * 0.3} ${L * 0.3} ${-h * 0.3} M ${-L} ${h * 0.6} Q ${-L * 0.3} ${h * 0.3} ${L * 0.3} ${h * 0.3} M ${L * 0.3 - h} ${-h} L ${L * 0.3} 0 L ${L * 0.3 - h} ${h}`;
          return (
            <g key={`wind-${k}`} transform={`translate(${p.x} ${p.y}) rotate(${angle})`} aria-hidden>
              <path
                d={windD}
                fill="none"
                stroke="#3a3a3a"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                opacity="0.55"
              />
            </g>
          );
        })}
        {/* W15 Cairn: target cells. A subtle dotted ring + a small colored
            center dot indicate "a tile of this color must land here." Drawn
            after gravity arrows but BEFORE tiles so a tile sitting on a
            target visually covers the marker (the satisfaction is implied
            by the tile being there); empty targets show the marker clearly.
            See docs/future/w15-cairn-spec.md §6.1.

            The dotted-outline approach reads pre-attentively as "this cell
            wants something specific" without competing with the tile fills
            for visual weight. Stroke opacity 0.5 matches the gravity arrow
            opacity for consistency across cell-attribute markers. */}
        {level.objective.kind === 'target' && level.objective.targets.map((t) => {
          const p = axialToPixel(t.coord);
          return (
            <g
              key={`target-${t.coord.q},${t.coord.r}`}
              transform={`translate(${p.x} ${p.y})`}
              aria-hidden
            >
              <path
                d={hexPath(size * 0.85)}
                fill="none"
                stroke="#3a3a3a"
                strokeWidth="1.5"
                strokeDasharray="3 3"
                opacity="0.5"
              />
              <circle
                cx={0}
                cy={0}
                r={size * 0.18}
                fill={tileFill(t.color)}
                opacity="0.6"
              />
            </g>
          );
        })}
        {[...board.tiles.values()].map((t) => {
          const p = axialToPixel(t.coord);
          const isSelected = t.id === selectedId;
          const isHint = t.id === hintTileId;
          return (
            <g
              key={t.id}
              className={`tile${isSelected ? ' selected' : ''}${isHint ? ' hint' : ''}`}
              transform={`translate(${p.x} ${p.y})`}
              onClick={(e) => { e.stopPropagation(); handleTileTap(t.id); }}
            >
              <path
                d={cornerPath}
                fill={t.state.kind === 'mixer' ? '#9C968B' : tileFill(t.color)}
                stroke="#FFFFFF22"
                strokeWidth="1.5"
              />
              {t.state.kind === 'charged' && (
                <path d={hexPath(size - 10)} fill="none" stroke="#FFFFFFCC" strokeWidth="1.5" />
              )}
              {t.state.kind === 'mixer' && (
                // W13 "Crucible" mechanic — mixers are colorless until
                // claimed by a slid tile that bumps them. Visual: three
                // small dots in different muted palette colors arranged
                // in a triangle, suggesting "this could become any of
                // several colors." Engine transforms this to a normal
                // tile of the slid tile's color when bumped.
                <g aria-hidden>
                  <circle cx={0}              cy={-size * 0.22} r={size * 0.11} fill="#C7613A" opacity="0.9" />
                  <circle cx={-size * 0.20}   cy={size * 0.10}  r={size * 0.11} fill="#3F6F8B" opacity="0.9" />
                  <circle cx={size * 0.20}    cy={size * 0.10}  r={size * 0.11} fill="#5E7A58" opacity="0.9" />
                </g>
              )}
              {t.state.kind === 'locked' && (
                // W11 "Keep" mechanic — locked tiles show a small padlock
                // glyph over the fill. Unlocks when an adjacent charged
                // tile of matching key color is placed (engine handles
                // the transition; this is purely visual while locked).
                // Glyph: charcoal-on-translucent-ivory pill at tile center.
                <g aria-hidden>
                  <rect
                    x={-size * 0.32}
                    y={-size * 0.06}
                    width={size * 0.64}
                    height={size * 0.48}
                    rx={size * 0.08}
                    fill="#F5F2EBE8"
                    stroke="#1C1C1E"
                    strokeWidth="1.2"
                  />
                  <path
                    d={`M ${-size * 0.18} ${-size * 0.06} L ${-size * 0.18} ${-size * 0.22} A ${size * 0.18} ${size * 0.18} 0 0 1 ${size * 0.18} ${-size * 0.22} L ${size * 0.18} ${-size * 0.06}`}
                    fill="none"
                    stroke="#1C1C1E"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                  {/* Keyhole — tiny dot + vertical stroke for the classic
                      "this is locked" glyph read. */}
                  <circle cx="0" cy={size * 0.18} r={size * 0.045} fill="#1C1C1E" />
                  <rect
                    x={-size * 0.02}
                    y={size * 0.2}
                    width={size * 0.04}
                    height={size * 0.13}
                    fill="#1C1C1E"
                  />
                </g>
              )}
              {t.state.kind === 'reactive' && (
                // W18 "Embers" mechanic — reactive tiles show a faint
                // pulsing inner outline that signals "this detonates if
                // it merges." The pulse is slow (3s loop) and subtle so
                // it doesn't compete with the tile's color for visual
                // weight — a "hum" rather than an alarm. Uses the
                // emberPulse keyframe in styles.css.
                <g aria-hidden>
                  <path
                    d={hexPath(size - 8)}
                    fill="none"
                    stroke="#FFFFFFCC"
                    strokeWidth="1.5"
                    className="ember-pulse"
                  />
                </g>
              )}
              {isSelected && (
                <path d={cornerPath} fill="none" stroke="var(--accent)" strokeWidth="2.5" />
              )}
              {isHint && (
                <path d={cornerPath} fill="none" stroke="#FFD166" strokeWidth="2.5" className="hint-pulse" />
              )}
              {preExistingMergeIds.has(t.id) && (
                <path
                  d={cornerPath}
                  fill="none"
                  stroke="var(--accent)"
                  strokeWidth="2.5"
                  className="tile-will-merge-ring"
                />
              )}
            </g>
          );
        })}
        {/* Merge / annihilation feedback — v0.3.1 accent-ring pulse.
            Quiet Paper spec: "the only reward animation is a subtle accent
            pulse." Each merged cell dissolves in place over 220ms; a single
            accent-color stroke ring expands and fades from the result coord
            over 360ms (chain annihilation gets 480ms + heavier stroke).
            No particles, no color-fill rings, no bright core. The wrapper-
            on-g / CSS-on-path split is still load-bearing (CSS transform
            on an SVG path overrides the SVG transform attribute — without
            the wrapper, cells would collapse onto resultCoord). */}
        <g key={`fx-${mergeFx.key}`} style={{ pointerEvents: 'none' }}>
          {mergeFx.events.map((ev, i) => {
            const p = axialToPixel(ev.resultCoord);
            const isChain = ev.chainDepth >= 2;
            const color = tileFill(ev.color);
            return (
              <g key={i} transform={`translate(${p.x} ${p.y})`}>
                {ev.cells.map((c, ci) => {
                  const cp = axialToPixel(c);
                  const dx = cp.x - p.x;
                  const dy = cp.y - p.y;
                  return (
                    <g key={ci} transform={`translate(${dx} ${dy})`}>
                      <path
                        d={cornerPath}
                        fill={color}
                        className="fx-cell-dissolve"
                      />
                    </g>
                  );
                })}
                <circle
                  r={size * 0.85}
                  className={`fx-accent-ring${isChain ? ' chain' : ''}`}
                />
              </g>
            );
          })}
        </g>
        {/* Loss-diagnostic overlay. Fires for ~1s right before the "out of
            moves" dialog. For each tile on the board we draw:
              - A color-matched pulse ring around the tile (sweeping the
                eye across everything still left).
              - A small count badge above the tile showing how many tiles
                of that same color are on the board. Badges with count < 3
                glow amber ("can't ever merge"); count ≥ 3 glow sage ("you
                had merges available"). Same diagnostic for all loss
                reasons — budget, deadlock, or unclearable — because the
                counts are always the thing the player wants to see.
            Tiles are staggered ~25ms per index so it reads as a sweep
            rather than a simultaneous flash. */}
        {lossIntroKey !== null && (() => {
          const counts = new Map<number, number>();
          for (const t of board.tiles.values()) {
            if (t.state.kind !== 'normal' && t.state.kind !== 'charged') continue;
            counts.set(t.color, (counts.get(t.color) ?? 0) + 1);
          }
          const tiles = [...board.tiles.values()];
          return (
            <g key={`loss-${lossIntroKey}`} style={{ pointerEvents: 'none' }}>
              {tiles.map((t, ti) => {
                const p = axialToPixel(t.coord);
                const count = counts.get(t.color) ?? 0;
                const canMerge = count >= 3;
                const ringColor = tileFill(t.color);
                const badgeFill = canMerge ? '#5E8C6B' : '#C78B3A';
                const delay = ti * 25;
                return (
                  <g key={`loss-${t.id}`} transform={`translate(${p.x} ${p.y})`}>
                    {/* Pulse ring — grows outward, fades. Uses wrapper-g
                        pattern so the ring CSS transform doesn't fight
                        any SVG translate attribute. */}
                    <circle
                      r={size * 0.78}
                      fill="none"
                      stroke={ringColor}
                      strokeWidth="2.5"
                      className="fx-loss-ring"
                      style={{ animationDelay: `${delay}ms` }}
                    />
                    {/* Count badge, positioned up and to the right of the
                        tile. Outer wrapper <g> holds the SVG translate;
                        inner <g> gets the CSS pop animation. */}
                    <g transform={`translate(${size * 0.55} ${-size * 0.55})`}>
                      <g className="fx-loss-badge" style={{ animationDelay: `${delay + 120}ms` }}>
                        <circle r={size * 0.3} fill={badgeFill} stroke="#FFFFFF" strokeWidth="1.5" />
                        <text
                          x="0"
                          y="0"
                          textAnchor="middle"
                          dominantBaseline="central"
                          fontSize={size * 0.4}
                          fontWeight="700"
                          fill="#FFFFFF"
                        >
                          {count}
                        </text>
                      </g>
                    </g>
                  </g>
                );
              })}
            </g>
          );
        })()}
        {/* v0.13.0: ambient HexCritter. Peeks out of an empty cell after
            45s of idle, looks around, retreats. Different species per
            world. Per-spec (docs/future/w15-w20-plan.md §2): silent,
            passive, retreats instantly on input. Empty-cell list is
            cellPositions filtered against board.tileAt — pure derived
            value, recomputes per render. The HexCritter component caches
            this in a ref so the idle-timer doesn't reset on every tile
            slide; only document-level user input resets it. */}
        <HexCritter
          worldIndex={level.world}
          emptyCells={cellPositionsInBoard
            .filter(({ c }) => !board.tileAt.has(coordKey(c)))
            .map(({ c }) => c)}
        />
      </svg>
      {selectedTile && selectedPixel && (
        <DirectionRing
          center={selectedPixel}
          bounds={bounds}
          legal={selectedDirs}
          onPick={handleSlide}
          onCancel={() => setSelectedId(null)}
        />
      )}
      {cascadeToast && (
        <div className="toast cascade-toast" role="status" aria-live="polite">
          {cascadeToast}
        </div>
      )}
    </div>
  );
}

interface RingProps {
  center: { x: number; y: number };
  bounds: { minX: number; minY: number; w: number; h: number };
  legal: HexDirection[];
  onPick: (dir: HexDirection) => void;
  onCancel: () => void;
}

function DirectionRing({ legal, onPick, onCancel }: RingProps) {
  const legalSet = new Set(legal);
  return (
    <div
      style={{
        position: 'fixed',
        bottom: 'calc(env(safe-area-inset-bottom) + 72px)',
        left: '50%',
        transform: 'translateX(-50%)',
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 52px)',
        gap: 8,
        padding: 12,
        background: 'var(--ivory)',
        border: '1px solid #00000018',
        borderRadius: 16,
        boxShadow: '0 10px 30px #00000018',
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}
    >
      {[
        ['NW', 'NE' as const],
        ['W', 'E' as const],
        ['SW', 'SE' as const],
      ].flatMap(([l, r], row) => [
        <DirBtn key={`${l}`} dir={l as HexDirection} enabled={legalSet.has(l as HexDirection)} onPick={onPick} />,
        <div key={`spacer-${row}`} />,
        <DirBtn key={`${r}`} dir={r as HexDirection} enabled={legalSet.has(r as HexDirection)} onPick={onPick} />,
      ])}
    </div>
  );
}

function DirBtn({ dir, enabled, onPick }: { dir: HexDirection; enabled: boolean; onPick: (d: HexDirection) => void }) {
  return (
    <button
      disabled={!enabled}
      aria-label={`slide ${dir}`}
      onClick={() => onPick(dir)}
      style={{ width: 52, height: 52, padding: 0, fontSize: 20 }}
    >
      {DIR_LABEL[dir]}
    </button>
  );
}

void hexCorners;
void DIRECTIONS;

// Pointy-top axial hex neighbor offsets — same as the engine's internal
// neighbors() helper but duplicated here so the UI can detect pre-existing
// merge groups without reaching into engine internals. Keep in sync with
// packages/engine/src/hex.ts if directions are ever re-indexed.
const HEX_NEIGHBOR_OFFSETS: ReadonlyArray<{ q: number; r: number }> = [
  { q: +1, r: 0 },   // E
  { q: -1, r: 0 },   // W
  { q: +1, r: -1 },  // NE
  { q: 0, r: -1 },   // NW
  { q: 0, r: +1 },   // SE
  { q: -1, r: +1 },  // SW
];

// Find connected components of same-color mergeable tiles with size >= 3.
// Returns arrays of tile IDs. Used at level-load to warn the player about
// pre-existing merge groups that will resolve on their first slide.
function findMergeableGroupsOnBoard(board: BoardT): number[][] {
  const byCoord = new Map<string, { id: number; color: number; coord: AxialCoord }>();
  for (const t of board.tiles.values()) {
    if (t.state.kind !== 'normal' && t.state.kind !== 'charged') continue;
    byCoord.set(coordKey(t.coord), { id: t.id, color: t.color, coord: t.coord });
  }
  const seen = new Set<number>();
  const groups: number[][] = [];
  for (const start of byCoord.values()) {
    if (seen.has(start.id)) continue;
    const group: number[] = [];
    const stack = [start];
    while (stack.length > 0) {
      const cur = stack.pop()!;
      if (seen.has(cur.id)) continue;
      seen.add(cur.id);
      group.push(cur.id);
      for (const d of HEX_NEIGHBOR_OFFSETS) {
        const nb = coordKey({ q: cur.coord.q + d.q, r: cur.coord.r + d.r });
        const n = byCoord.get(nb);
        if (n && n.color === cur.color && !seen.has(n.id)) stack.push(n);
      }
    }
    if (group.length >= 3) groups.push(group);
  }
  return groups;
}
